import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes } from 'react-icons/fa';

interface HeaderProps {
  isStatic?: boolean;
}

const MAIN_APP_URL = process.env.REACT_APP_MAIN_APP_URL || 'https://app.multigym.com.ar';


const Header: React.FC<HeaderProps> = ({ isStatic = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Cerrar el dropdown al hacer clic afuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Control del scroll para cambiar la apariencia del header
  useEffect(() => {
    if (isStatic) {
      setIsScrolled(true);
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStatic]);

  const textColor = 'text-white';
  const hoverColor = 'hover:text-blue-200';

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled || isStatic ? 'bg-blue-600 shadow-md py-2' : 'bg-transparent py-4'
      }`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img
            src="/img/logomultigym_h_b.png"
            alt="MultiGym"
            className="h-12"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.style.display = 'none';
            }}
          />
          <span className="text-2xl font-black text-white tracking-wider ml-2">MultiGym</span>
        </a>

        {/* Menú de navegación - Desktop */}
        <nav className="hidden md:flex space-x-8">
          <a href="/#home" className={`${textColor} ${hoverColor} font-medium transition-colors`}>Inicio</a>
          <a href="/#features" className={`${textColor} ${hoverColor} font-medium transition-colors`}>Características</a>
          <a href="/#register" className={`${textColor} ${hoverColor} font-medium transition-colors`}>Registro</a>
          <a href="/#download" className={`${textColor} ${hoverColor} font-medium transition-colors`}>Descarga la App</a>
          <a href="/#contact" className={`${textColor} ${hoverColor} font-medium transition-colors`}>Contacto</a>
        </nav>

        {/* Botón de Acceso único con Dropdown - Desktop */}
        <div className="hidden md:flex items-center relative dropdown-container">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="bg-white text-blue-600 px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition-all shadow-md flex items-center space-x-1"
          >
            <span>Acceso</span>
            <svg className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
              <a
                href={`${MAIN_APP_URL}/acceso`}
                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-semibold transition-colors"
              >
                Portal de Socios
              </a>
              <a
                href={`${MAIN_APP_URL}/login`}
                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-semibold border-t border-gray-50 transition-colors"
              >
                Panel de Gimnasios
              </a>
            </div>
          )}
        </div>

        {/* Botón de menú móvil */}
        <button
          className="md:hidden text-white"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>
      </div>

      {/* Menú móvil */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex flex-col space-y-3">
              <a href="/#home" className="text-gray-900 py-2 border-b border-gray-100" onClick={() => setIsMenuOpen(false)}>Inicio</a>
              <a href="/#features" className="text-gray-900 py-2 border-b border-gray-100" onClick={() => setIsMenuOpen(false)}>Características</a>
              <a href="/#register" className="text-gray-900 py-2 border-b border-gray-100" onClick={() => setIsMenuOpen(false)}>Registro</a>
              <a href="/#download" className="text-gray-900 py-2 border-b border-gray-100" onClick={() => setIsMenuOpen(false)}>Descarga la App</a>
              <a href="/#contact" className="text-gray-900 py-2" onClick={() => setIsMenuOpen(false)}>Contacto</a>

              <div className="flex flex-col space-y-2 pt-2 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase px-1">Acceso al Sistema</p>
                <a
                  href={`${MAIN_APP_URL}/acceso`}
                  className="bg-gray-50 text-blue-600 border border-gray-200 px-6 py-3 rounded-full font-bold text-center hover:bg-blue-50/50 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Acceso Socios
                </a>
                <a
                  href={`${MAIN_APP_URL}/login`}
                  className="bg-blue-600 text-white px-6 py-3 rounded-full font-bold text-center hover:bg-blue-700 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Panel Gimnasios
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
